import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosServices from 'src/utils/axios';
import { getMe } from 'src/services/userService';
import { decodeAccessToken } from 'src/utils/jwt';

interface AuthState {
  accessToken: string | null;
  fullName: string | null;
  email: string | null;
  avatarUrl: string | null;
  role: string | null;
  roleId: number | null;
  mustChangePassword: boolean;
  loading: boolean;
  error: string | null;
}

const initialAccessToken = localStorage.getItem('accessToken');
const initialPayload = initialAccessToken ? decodeAccessToken(initialAccessToken) : null;

const initialState: AuthState = {
  accessToken: initialAccessToken,
  role: initialPayload?.role ?? null,
  roleId: initialPayload?.roleId ?? null,
  fullName: localStorage.getItem('fullName'),
  email: localStorage.getItem('email'),
  avatarUrl: localStorage.getItem('avatarUrl'),
  mustChangePassword: false,
  loading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post('/auth/login', credentials);
      const accessToken = response.data.accessToken as string;

      // Persist before calling /user/me so the axios auth interceptor picks it up
      localStorage.setItem('accessToken', accessToken);

      let roleId: number | null = null;
      let role: string | null = null;
      let fullName: string | null = null;
      let email: string | null = null;
      let avatarUrl: string | null = null;
      let mustChangePassword = false;
      try {
        const me = await getMe();
        roleId = me.roleId ?? null;
        role = me.roleName ?? null;
        fullName = me.fullName ?? null;
        email = me.email ?? null;
        avatarUrl = me.avatarUrl ?? null;
        mustChangePassword = me.mustChangePassword ?? false;
      } catch (meError) {
        // El login ya es válido aunque no podamos resolver el perfil; no debe bloquear el ingreso.
        console.error('No se pudo obtener el perfil del usuario', meError);
      }

      return { accessToken, roleId, role, fullName, email, avatarUrl, mustChangePassword };
    } catch (error: any) {

      return rejectWithValue(error?.message || 'Credenciales incorrectas');
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await axiosServices.post('/auth/logout');
  } catch (error) {
    console.error('Error en backend logout', error);
  } finally {
    clearIdentityStorage();
    sessionStorage.clear();
  }
});

const clearIdentityStorage = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('roleId');
  localStorage.removeItem('role');
  localStorage.removeItem('fullName');
  localStorage.removeItem('email');
  localStorage.removeItem('avatarUrl');
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    logoutLocal: (state) => {
      state.accessToken = null;
      state.fullName = null;
      state.email = null;
      state.avatarUrl = null;
      state.role = null;
      state.roleId = null;
      state.mustChangePassword = false;
      clearIdentityStorage();
      localStorage.removeItem('accessToken');
      sessionStorage.clear();
    },
    setMustChangePassword: (state, action: PayloadAction<boolean>) => {
      state.mustChangePassword = action.payload;
    },
    setIdentity: (state, action: PayloadAction<{ fullName?: string | null; email?: string | null; avatarUrl?: string | null }>) => {
      if (action.payload.fullName !== undefined) {
        state.fullName = action.payload.fullName;
        if (action.payload.fullName !== null) localStorage.setItem('fullName', action.payload.fullName);
        else localStorage.removeItem('fullName');
      }
      if (action.payload.email !== undefined) {
        state.email = action.payload.email;
        if (action.payload.email !== null) localStorage.setItem('email', action.payload.email);
        else localStorage.removeItem('email');
      }
      if (action.payload.avatarUrl !== undefined) {
        state.avatarUrl = action.payload.avatarUrl;
        if (action.payload.avatarUrl !== null) localStorage.setItem('avatarUrl', action.payload.avatarUrl);
        else localStorage.removeItem('avatarUrl');
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.accessToken = action.payload.accessToken;
        const payload = decodeAccessToken(action.payload.accessToken);
        state.role = payload?.role ?? null;
        state.roleId = payload?.roleId ?? null;
        state.fullName = action.payload.fullName;
        state.email = action.payload.email;
        state.avatarUrl = action.payload.avatarUrl;
        state.mustChangePassword = action.payload.mustChangePassword;
        localStorage.setItem('accessToken', action.payload.accessToken);
        if (action.payload.roleId !== null) localStorage.setItem('roleId', String(action.payload.roleId));
        if (action.payload.role !== null) localStorage.setItem('role', action.payload.role);
        if (action.payload.fullName !== null) localStorage.setItem('fullName', action.payload.fullName);
        if (action.payload.email !== null) localStorage.setItem('email', action.payload.email);
        if (action.payload.avatarUrl !== null) localStorage.setItem('avatarUrl', action.payload.avatarUrl);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(logout.fulfilled, (state) => {
        state.accessToken = null;
        state.role = null;
        state.roleId = null;
        state.fullName = null;
        state.email = null;
        state.avatarUrl = null;
        state.mustChangePassword = false;
        clearIdentityStorage();
        sessionStorage.clear();
      })
      .addCase(logout.rejected, (state) => {
        state.accessToken = null;
        state.role = null;
        state.roleId = null;
        state.fullName = null;
        state.email = null;
        state.avatarUrl = null;
        state.mustChangePassword = false;
        clearIdentityStorage();
        sessionStorage.clear();
      });
  },
});

export const { clearError, logoutLocal, setIdentity, setMustChangePassword } = authSlice.actions;
export default authSlice.reducer;
